import React, { useRef, useState, useEffect } from 'react';
import SearchBox from '../../search-box/jsx/SearchBox';
import PropTypes from 'prop-types';
import { XssSafeContent } from '@squiz/xaccel-xss-safe-content';
import autoCompleteTemplateFn from '../../search-box/jsx/templates/templates';
import styles from '../css/functional.module.scss';

/**
 * The primary function to generate a JSX Hero banner.
 * @param {String} palette Which colour palette to set on the component.
 * @param {String} float Control where to place the content on the hero banner.
 * @param {Object} bgImageDesktop Background Image to use for desktop view.
 * @param {Object} bgImageTabletLand Background Image to use for tablet Landscape view.
 * @param {Object} bgImageTabletPort Background Image to use for tablet Port view.
 * @param {Object} bgImageMobile Background Image to use for mobile view.
 * @param {String} heroText The content for the hero banner.
 * @param {String} imagePos Control how to display the background image.
 * @param {String} overlayPer Control setting an overlay over the hero banner.
 * @param {Boolean} bgContentColour Control setting a background to the text content area.
 * @param {String} bgHeight Control the height of the hero banner.
 * @param {Boolean} scrollButton A control to show or hide a scroll down button.
 * @param {String} labelType The version of the label that determines whether to display an icon or text.
 * @param {String} labelText The label text that should be displayed.
 * @param {String} inputName The name attribute of the input field.
 * @param {Boolean} showClearBtn Flag to determine whether to show the clear button.
 * @param {String} submitType The version of the submit button that determines whether to display an icon or text.
 * @param {String} submitText The text of submit button to render.
 * @param {String} placeholder The default text to render in input field.
 * @param {Boolean} autofocus Flag to determine whether autofocus on input should be enabled.
 * @param {String} resultPage On submit, send the form-data to the html page to process the input.
 * @param {Boolean} autocompleteLoader Flag to determine whether to show the loader.
 * @param {String} autocompleteEmphasis Phrase highlight version for autocomplete.
 * @param {String} autocompleteTemplates A string labelling which template/templates to use.
 * @returns {JSX.Element}
 */
export default function HeroSearchBanner({
    palette,
    float,
    bgImageDesktop,
    bgImageTabletLand,
    bgImageTabletPort,
    bgImageMobile,
    heroText,
    imagePos,
    overlayPer,
    bgContentColour,
    bgHeight,
    scrollButton,
    labelType,
    labelText,
    inputName,
    showClearBtn,
    submitType,
    submitText,
    placeholder,
    autofocus,
    resultPage,
    autocompleteLoader,
    autocompleteEmphasis,
    autocompleteTemplates,
}) {
    const classes = [
        'no-wysiwyg',
        `${styles.heroBanner}`,
        'hero-banner',
        'hero-banner--settings',
        `hero-banner--overlay-${overlayPer}`,
        `${styles[bgHeight]}`,
        `hero-banner--bg-${bgHeight}`,
        `hero-banner--${palette}`,
        `hero-banner--pull-${float}`,
    ].join(' ');

    // Grab the templates from the Search Box
    const templates = autoCompleteTemplateFn();

    const [bannerHeight, setBannerHeight] = useState(0);
    const heroBannerRef = useRef(null);

    // Set the initial height on first full render
    useEffect(() => {
        setBannerHeight(heroBannerRef.current.clientHeight);
    }, []);

    // If props change update the height
    useEffect(() => {
        setBannerHeight(heroBannerRef.current.clientHeight);
    }, [bgHeight]);

    const scrollTo = () => {
        window.scrollBy(0, bannerHeight);
    };

    // Set the correct template depending on what the user selects in the UI
    const [autoCompleteTemplateOption, setAutoCompleteTemplateOption] = useState([]);
    useEffect(() => {
        switch (autocompleteTemplates) {
            case 'none':
                setAutoCompleteTemplateOption([]);
                break;
            case 'autocomplete':
                setAutoCompleteTemplateOption([templates[0]]);
                break;
            case 'concierge':
                setAutoCompleteTemplateOption(templates);
                break;
            default:
                setAutoCompleteTemplateOption([templates[0]]);
        }
    }, []);

    return (
        <div
            ref={heroBannerRef}
            title="Hero Search Banner Section"
            className={`${classes}
            ${bgContentColour ? 'hero-banner--bg-solid' : ''}
            `}
        >
            <div className={`hero-banner-wrapper ${styles.heroBannerWrapper}`}>
                <div
                    className={`hero-banner__contents-container ${styles.heroBannerContentsContainer} ${styles.heroBannerContentsContainerSearch}`}
                >
                    <div className={`hero-banner__contents ${styles.heroBannerContents}`} title="Hero Search Banner Content">
                        <XssSafeContent content={heroText} className="hero-banner__contents-text" elementType="div" />
                        <div className={`${styles.search} hero-search-banner__search`}>
                            <SearchBox
                                labelType={labelType}
                                labelText={labelText}
                                inputName={inputName}
                                showClearBtn={showClearBtn}
                                submitType={submitType}
                                submitText={submitText}
                                placeholder={placeholder}
                                autofocus={autofocus}
                                resultPage={resultPage}
                                autocompleteLoader={autocompleteLoader}
                                autocompleteEmphasis={autocompleteEmphasis}
                                autocompleteTemplates={autoCompleteTemplateOption}
                            />
                        </div>
                    </div>
                </div>
                {bgImageDesktop?.imageVariations?.original?.url && (
                    <img
                        src={bgImageDesktop?.imageVariations?.original?.url}
                        alt={bgImageDesktop?.alt}
                        className={`hero-banner__image hero-banner__image--${imagePos} ${styles.heroBannerImage}`}
                        srcSet={`${bgImageDesktop?.imageVariations?.original?.url} 1600w, ${
                            bgImageTabletLand?.imageVariations?.original?.url || bgImageDesktop?.imageVariations?.original?.url
                        } 1000w, ${
                            bgImageTabletPort?.imageVariations?.original?.url || bgImageDesktop?.imageVariations?.original?.url
                        } 800w, ${bgImageMobile?.imageVariations?.original?.url || bgImageDesktop?.imageVariations?.original?.url} 450w`}
                        sizes="100vw"
                    />
                )}
            </div>
            {scrollButton && (
                <button
                    className={`${styles.heroBannerScroll} hero-banner__js-scroll-btn`}
                    type="button"
                    onClick={() => {
                        scrollTo();
                    }}
                >
                    <svg>
                        <title>Down Chevron</title>
                        <use href="#chevron" />
                    </svg>
                </button>
            )}
        </div>
    );
}

// These props should reflect the configuration that happens in Matrix
HeroSearchBanner.propTypes = {
    float: PropTypes.oneOf(['none', 'left', 'right', 'centered']),
    bgContentColour: PropTypes.bool,
    bgHeight: PropTypes.oneOf(['full', 'compact', 'fixed']),
    overlayPer: PropTypes.oneOf(['0', '10', '20', '30', '40', '50', 'bright-10', 'bright-20', 'bright-30', 'bright-40', 'bright-50']),
    palette: PropTypes.oneOf(['palette-a', 'palette-b', 'palette-c', 'palette-d']),
    imagePos: PropTypes.oneOf([
        'top',
        'center',
        'bottom',
        'right',
        'left',
        'center-center',
        'left-top',
        'left-bottom',
        'left-center',
        'right-top',
        'right-bottom',
        'center-top',
        'center-bottom',
    ]),
    heroText: PropTypes.string,
    bgImageDesktop: PropTypes.shape({
        name: PropTypes.string,
        alt: PropTypes.string,
        caption: PropTypes.string,
        imageVariations: PropTypes.shape({
            original: PropTypes.shape({
                url: PropTypes.string,
                width: PropTypes.number,
                height: PropTypes.number,
                byteSize: PropTypes.number,
                mimeType: PropTypes.string,
                aspectRatio: PropTypes.string,
                sha1Hash: PropTypes.string,
            }),
        }),
    }),
    bgImageTabletLand: PropTypes.shape({
        name: PropTypes.string,
        alt: PropTypes.string,
        caption: PropTypes.string,
        imageVariations: PropTypes.shape({
            original: PropTypes.shape({
                url: PropTypes.string,
                width: PropTypes.number,
                height: PropTypes.number,
                byteSize: PropTypes.number,
                mimeType: PropTypes.string,
                aspectRatio: PropTypes.string,
                sha1Hash: PropTypes.string,
            }),
        }),
    }),
    bgImageTabletPort: PropTypes.shape({
        name: PropTypes.string,
        alt: PropTypes.string,
        caption: PropTypes.string,
        imageVariations: PropTypes.shape({
            original: PropTypes.shape({
                url: PropTypes.string,
                width: PropTypes.number,
                height: PropTypes.number,
                byteSize: PropTypes.number,
                mimeType: PropTypes.string,
                aspectRatio: PropTypes.string,
                sha1Hash: PropTypes.string,
            }),
        }),
    }),
    bgImageMobile: PropTypes.shape({
        name: PropTypes.string,
        alt: PropTypes.string,
        caption: PropTypes.string,
        imageVariations: PropTypes.shape({
            original: PropTypes.shape({
                url: PropTypes.string,
                width: PropTypes.number,
                height: PropTypes.number,
                byteSize: PropTypes.number,
                mimeType: PropTypes.string,
                aspectRatio: PropTypes.string,
                sha1Hash: PropTypes.string,
            }),
        }),
    }),
    scrollButton: PropTypes.bool,
    labelType: PropTypes.oneOf(['icon', 'text']),
    labelText: PropTypes.string,
    inputName: PropTypes.string,
    showClearBtn: PropTypes.bool,
    submitType: PropTypes.oneOf(['icon', 'text']),
    submitText: PropTypes.string,
    placeholder: PropTypes.string,
    autofocus: PropTypes.bool,
    resultPage: PropTypes.string,
    autocompleteLoader: PropTypes.bool,
    autocompleteEmphasis: PropTypes.oneOf(['none', 'query', 'notquery']),
    autocompleteTemplates: PropTypes.oneOf(['none', 'autocomplete', 'concierge']),
};

HeroSearchBanner.defaultProps = {
    float: null,
    bgContentColour: null,
    bgHeight: null,
    overlayPer: null,
    palette: null,
    imagePos: null,
    bgImageDesktop: null,
    bgImageTabletLand: null,
    bgImageTabletPort: null,
    bgImageMobile: null,
    heroText: null,
    scrollButton: null,
    labelType: 'icon',
    labelText: null,
    inputName: 'query',
    showClearBtn: true,
    submitType: 'icon',
    submitText: null,
    placeholder: 'Search for something...',
    autofocus: true,
    resultPage: './search.html',
    autocompleteLoader: true,
    autocompleteEmphasis: 'query',
    autocompleteTemplates: 'autocomplete',
};
